import axios from "axios";
import { defineStore } from "pinia";
import { useConversation } from "./Conversation";
import { useWorkspace } from "./Workspaces";
import { useWorkspaceTree } from "./WorkspaceTree";
import { useTeam } from "../Components/Custom/Team/Store/Team";
import Utilities from "../Helpers/utilities";
import { useUserStore } from "./User";
import HttpStatusCodes from "Admin/js/Constants/http.js";
import { useAppState } from "./AppState";
import { useChat } from "../Components/Chat/Store/Chat";
import { isRequestCancelled } from "../Helpers/requestCancellation";

export const useInvitation = defineStore("InvitationStore", {
    state: () => ({
        show_invite_model: false,
        user_invitations: [],
        user_profile_ids: [],
        selected_user_profile_ids: [],
        // selected_group_ids: [],
        selected_group_user_profile_ids: [],
        is_retrieving_invited_user_in_progress: false,
        user_profiles_with_roles: [],
        has_conversation: false,
        RESOURCE_TYPES: {
            item: "items",
            "item-group": "item-groups",
            board: "boards",
            "board-group": "board-groups",
            workspace: "workspaces",
            "workspace-group": "workspace-groups",
        },
        // please don't change the order of the data
        ACCESS_LEVELS: {
            ITEM: "item",
            ITEM_GROUP: "item-group",
            BOARD: "board",
            BOARD_GROUP: "board-group",
            WORKSPACE: "workspace",
            WORKSPACE_GROUP: "workspace-group",
        },
        ROLES: {
            OWNER: "owner",
            ADMIN: "admin",
            MEMBER: "member",
            OBSERVER: "observer",
        },
        active_space_id: null,
        active_level_titles: {},
        current_participants: [],
        temp_roles_change_status: {},
        temp_chat_access_status: {},
        selected_ids_for_invite: new Set(),
        unselected_ids_for_remove: new Set(),
        current_user_ids: [],
        has_fetched_members_with_role_from_quick_conversation: false,
        quick_conversation_selected_users: [],
        quick_conversation_assigned_user: null,
    }),
    getters: {
        active_conversation_owner_id: (state) => {
            if (!state.active_space_id) {
                return null;
            }

            return (
                useWorkspaceTree().getWorkspaceById(state.active_space_id)
                    ?.owner_id ?? null
            );
        },
        new_modified_participants: (state) => {
            return (
                state.user_profiles_with_roles
                    .filter((user_profile) => {
                        return (
                            user_profile.is_conversation_participant ||
                            user_profile.role === state.ROLES.OBSERVER
                        );
                    })
                    .map((user_profile) => user_profile?.id) ?? []
            );
        },
        is_anyones_roles_changed: (state) => {
            return Object.values(state.temp_roles_change_status ?? {}).includes(
                true,
            );
        },
        is_anyones_chat_access_changed: (state) => {
            return Object.values(state.temp_chat_access_status ?? {}).includes(
                true,
            );
        },
        user_can_edit: (state) => {
            const user_profile = state.user_profiles_with_roles.find(
                (user) => user?.id === useUserStore().profile?.id,
            );
            if (!user_profile) return false;
            return [state.ROLES.OWNER, state.ROLES.ADMIN].includes(
                user_profile?.role,
            );
        },
    },
    actions: {
        closeInviteModel() {
            this.show_invite_model = false;
        },
        async getUserInvitations() {
            try {
                const { data } = await axios.get("/user/invitations");
                this.user_invitations = data?.data.lists;
            } catch (error) {
                console.log("error in console");
            }
        },
        resetInviteProfiles() {
            const all_profile_ids = this.selected_user_profile_ids.concat([
                ...new Set(this.selected_group_user_profile_ids),
            ]);
            this.user_profile_ids = [...new Set(all_profile_ids)];
        },
        restoreSelectedInviteProfiles() {
            this.selected_user_profile_ids = this.user_profile_ids;
        },
        clearSelectedInviteProfiles() {
            this.user_profile_ids = [];
            this.selected_user_profile_ids = [];
            this.selected_group_user_profile_ids = [];
        },
        setProfileIds(val) {
            this.user_profile_ids = val;
        },

        async getInvitedMembers(
            conversation,
            additionalResource = null,
            requestConfig = {},
        ) {
            const resource_type =
                additionalResource?.type ??
                this.RESOURCE_TYPES[conversation?.type];
            try {
                return await axios.get(
                    `/invites/chin/${resource_type}/${additionalResource?.type_id ?? conversation?.type_id}`,
                    requestConfig,
                );
            } catch (e) {
                if (isRequestCancelled(e)) {
                    console.log("request cancelled", e);
                    return;
                }
                if (
                    e.response?.status === HttpStatusCodes.UNPROCESSABLE_ENTITY
                ) {
                    Utilities().showErrorSnackbar(
                        "This conversation is no longer available.",
                    );
                }
                console.log("getting invitation profile ids");
            }
        },
        async invitedProfiles(optionalResource = null, requestConfig = {}) {
            this.is_retrieving_invited_user_in_progress = true;

            //reset previous profile ids if any
            this.setProfileIds([]);

            let invited_profile_ids = [];
            const conversation = useConversation().active;

            if (
                Object.keys(useInvitation().RESOURCE_TYPES).includes(
                    conversation?.type,
                )
            ) {
                try {
                    const res = await this.getInvitedMembers(
                        conversation,
                        optionalResource,
                        requestConfig,
                    );
                    if (!res) {
                        this.is_retrieving_invited_user_in_progress = false;
                        return false;
                    }
                    const responseData = res?.data?.data;

                    const hasConversation = responseData?.has_conversation;
                    const participants = responseData?.participants?.length;

                    if (hasConversation) {
                        invited_profile_ids = responseData?.participants || [];
                    } else if (responseData?.invited_profile_ids?.length) {
                        invited_profile_ids = responseData?.invited_profile_ids;
                    } else if (responseData?.all_profile_ids) {
                        invited_profile_ids = responseData?.all_profile_ids;
                    }
                } catch (e) {
                    if (isRequestCancelled(e)) {
                        this.is_retrieving_invited_user_in_progress = false;
                        console.log("request cancelled", e);
                        return false;
                    }
                    if (
                        e.response?.status ===
                        HttpStatusCodes.UNPROCESSABLE_ENTITY
                    ) {
                        Utilities().showErrorSnackbar(
                            "This conversation is no longer available.",
                        );
                    }

                    Utilities().showErrorSnackbar(
                        "Failed to retrieve existing invitation",
                    );
                    console.log(e);
                    this.is_retrieving_invited_user_in_progress = false;
                    return false;
                }
            }

            if (conversation?.is_group || conversation?.is_group_one_off) {
                invited_profile_ids = conversation.getActiveParticipantIds();
            }

            if (!invited_profile_ids) {
                invited_profile_ids = [];
            }

            //invited_profile_ids.push(useWorkspace().getSpaceOwnerId())
            invited_profile_ids = Array.from(new Set(invited_profile_ids));

            this.setProfileIds(invited_profile_ids);

            this.restoreSelectedInviteProfiles();
            this.is_retrieving_invited_user_in_progress = false;
            return true;
        },
        async fetchMembersWithRole(
            resource_type,
            resource_id,
            requestConfig = {},
        ) {
            try {
                this.temp_roles_change_status = {};
                this.temp_chat_access_status = {};

                useTeam().setAssigned(null);
                useTeam().setAssigner(null);
                useTeam().setOriginalAssigned(null);

                const res = await axios.get(
                    `invites/${resource_type}/${resource_id}`,
                    requestConfig,
                );

                useTeam().setAssigned(
                    res?.data?.data?.assigned_user_profile_id,
                );
                useTeam().setAssigner(
                    res?.data?.data?.assigner_user_profile_id,
                );
                useTeam().setOriginalAssigned(
                    res?.data?.data?.assigned_user_profile_id,
                );
                this.has_conversation = res?.data?.data?.has_conversation;
                this.active_level_titles = res?.data?.data?.resource;
                this.user_profiles_with_roles =
                    res?.data?.data?.user_profiles?.map((user_profile) => {
                        const modified_user_profile = {
                            id: user_profile?.id,
                            role: user_profile?.role,
                            access_level: "",
                            is_conversation_participant: this.has_conversation
                                ? user_profile?.is_conversation_participant
                                : true,
                            has_parent_access: user_profile?.has_parent_access,
                        };

                        for (
                            let i = 0;
                            i < user_profile?.roles?.length ?? [].length;
                            i++
                        ) {
                            const access_level =
                                user_profile?.roles[i]?.model_type;

                            modified_user_profile.access_level = access_level;

                            const values = Object.values(this.ACCESS_LEVELS);
                            const access_level_index = values.findIndex(
                                (el) => el === access_level,
                            );

                            if (access_level_index === values.length - 1) {
                                break;
                            }
                        }

                        return modified_user_profile;
                    });

                this.current_participants =
                    res?.data?.data?.user_profiles
                        .filter(
                            (user_profile) =>
                                user_profile?.is_conversation_participant,
                        )
                        .map((user_profile) => user_profile?.id) ?? [];
                this.current_user_ids =
                    res?.data?.data?.user_profiles.map(
                        (user_profile) => user_profile?.id,
                    ) ?? [];

                this.selected_ids_for_invite.clear();
                this.unselected_ids_for_remove.clear();
                return true;
            } catch (error) {
                if (isRequestCancelled(error)) {
                    console.log("request cancelled", error);
                    return false;
                }
                if (
                    error.response?.status ===
                    HttpStatusCodes.UNPROCESSABLE_ENTITY
                ) {
                    Utilities().showErrorSnackbar(
                        "This conversation is no longer available.",
                    );
                }
                console.log(error);
                return false;
            }
        },
        updateRole(role, user_id) {
            const findedUser = this.user_profiles_with_roles?.find(
                (user_profile) => user_profile?.id === user_id,
            );
            if (findedUser) {
                findedUser.role = role;
            }
        },
        updateParticipantAccess(user_id, is_participant) {
            const findedUser = this.user_profiles_with_roles?.find(
                (user_profile) => user_profile?.id === user_id,
            );
            if (findedUser) {
                findedUser.is_conversation_participant = is_participant;
            }
        },
        addFakeMemberWithRoleIfNotExist(member, access_level) {
            const profileIds =
                this.user_profiles_with_roles?.map(
                    (user_profile) => user_profile.id,
                ) ?? [];
            if (!profileIds.includes(member?.id)) {
                const fake_profile = {
                    id: member?.id,
                    role: this.ROLES.MEMBER,
                    access_level,
                    is_conversation_participant: true,
                    has_parent_access: false,
                };
                if (member.non_registered) {
                    fake_profile.email = member.email;
                    fake_profile.non_registered = true;
                }
                this.user_profiles_with_roles.push(fake_profile);

                if (!member.is_agent) {
                    if (!profileIds.includes(member?.agent_id)) {
                        const agentProfile = {
                            ...fake_profile,
                            id: member?.agent_id,
                        };
                        this.user_profiles_with_roles.push(agentProfile);
                    }
                }
            }
        },
        removeMemberWithRole(member_id) {
            const userProfiles = useUserStore().profiles;

            const member = userProfiles.find(
                (user_profile) => user_profile?.id === member_id,
            );
            const removeIds = [member_id, member?.agent_id];

            this.user_profiles_with_roles =
                this.user_profiles_with_roles.filter((user_profile) => {
                    return !removeIds.includes(user_profile?.id);
                });
        },
        isUserParticipant(user_id) {
            return Boolean(
                this.user_profiles_with_roles.find(
                    (user) => user.id === user_id,
                ),
            );
        },

        canEditRoleBasedOnLevel(current_conv_type, user_highest_access) {
            const access_levels = Object.values(this.ACCESS_LEVELS ?? {});
            const conv_order_index = access_levels.findIndex(
                (access_level) => current_conv_type === access_level,
            );
            const user_access_order_idnex = access_levels.findIndex(
                (access_level) => user_highest_access === access_level,
            );
            return conv_order_index >= user_access_order_idnex;
        },
        updateUserAddRemoveTrackStatus(user_id, checked) {
            if (checked) {
                if (!this.current_user_ids.includes(user_id)) {
                    this.selected_ids_for_invite.add(user_id);
                } else {
                    this.unselected_ids_for_remove.delete(user_id);
                }
            } else {
                if (this.current_user_ids.includes(user_id)) {
                    this.unselected_ids_for_remove.add(user_id);
                } else {
                    this.selected_ids_for_invite.delete(user_id);
                }
            }
        },
        canShowItemIcon(type) {
            return [
                this.ACCESS_LEVELS.ITEM,
                this.ACCESS_LEVELS.ITEM_GROUP,
            ].includes(type);
        },

        canShowBoardIcon(type) {
            return [this.ACCESS_LEVELS.BOARD].includes(type);
        },

        canShowSpaceIcon(type) {
            return [
                this.ACCESS_LEVELS.WORKSPACE,
                this.ACCESS_LEVELS.WORKSPACE_GROUP,
                this.ACCESS_LEVELS.BOARD_GROUP,
            ].includes(type);
        },

        async unInviteMembers(
            resource_type,
            resource_id,
            user_ids,
            resource_name,
            ignore_invitation_profile_ids = false,
        ) {
            const workspace_store = useWorkspace();
            try {
                workspace_store.setInvitationInProgress(true);
                const { data } = await axios.post(
                    `un-invites/${resource_type}/${resource_id}`,
                    {
                        user_profile_ids: user_ids,
                    },
                );

                const optionalPayload = {
                    type: resource_type,
                    type_id: resource_id,
                };

                if (!ignore_invitation_profile_ids) {
                    await this.invitedProfiles(optionalPayload);
                }
                if (!resource_name) {
                    Utilities().showSuccessSnackbar(
                        data.message,
                        null,
                        "success",
                        5000,
                    );
                } else {
                    Utilities().showInfoSnackbar(
                        data.message,
                        null,
                        "info",
                        5000,
                    );
                }

                return true;
            } catch (error) {
                console.log(error);
                const api_message = error.response?.data?.message;
                if (typeof api_message === "string" && api_message.length > 0) {
                    Utilities().showErrorSnackbar(api_message);
                }
                return false;
            } finally {
                workspace_store.setInvitationInProgress(false);
            }
        },
        resetTempChangeTracking() {
            this.temp_roles_change_status = {};
            this.temp_chat_access_status = {};
        },

        addSelectedUserToQuickConversation(user_id) {
            if (!this.quick_conversation_selected_users.includes(user_id)) {
                this.quick_conversation_selected_users.push(user_id);
            }
        },

        resetQuickConversation() {
            this.has_fetched_members_with_role_from_quick_conversation = false;
            this.quick_conversation_selected_users = [];
            this.quick_conversation_assigned_user = null;
        },
        setQuickConversationCheckedUsersAndAssigneeToUserProfilesWithRoles() {
            const profileIdsSet = new Set();

            const userProfiles = useUserStore().getProfilesByIds(
                this.quick_conversation_selected_users,
            );
            userProfiles.forEach((userProfile) => {
                profileIdsSet.add(userProfile.id);
                if (userProfile.agent_id) {
                    profileIdsSet.add(userProfile.agent_id);
                }
            });

            this.user_profiles_with_roles = this.user_profiles_with_roles.map(
                (user_profile) => {
                    return {
                        ...user_profile,
                        is_conversation_participant: profileIdsSet.has(
                            user_profile.id,
                        ),
                    };
                },
            );
            if (this.quick_conversation_assigned_user) {
                useTeam().assignedTo(this.quick_conversation_assigned_user);
            }
        },
        openMembershipPanelForQuickConversation() {
            useChat().setPreviousConvID(useConversation().active?.id);
            useChat().setMemberTab();
            useAppState().setSelectedVariantTab("members");
            useAppState().setPreviousSelectedVariantTab("members");
        },
    },
});
